import { useState } from 'react';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PositionSchema, type PositionDto } from '@taqon/contracts';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

const POSITIONS = '/api/v1/hr/positions';

/**
 * Positions: HR reference data (FRD-HRP-009; ERD §7.3 "Position — title"). A title and an active
 * / inactive status; no grades, bands or headcount, which no Release 1 source defines.
 */
export function PositionsPage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'hr.position:create');
  const canChange = hasCapability(session.data, 'hr.position:change');
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [editing, setEditing] = useState<PositionDto | null>(null);

  const positions = useQuery({
    queryKey: ['hr', 'positions'],
    queryFn: () => getPage(`${POSITIONS}?pageSize=200`, PositionSchema),
  });
  const create = useMutation({
    mutationFn: () =>
      postJson(POSITIONS, { title: title.trim() }, PositionSchema, session.data?.csrfToken ?? ''),
    onSuccess: async () => {
      setOpen(false);
      setTitle('');
      await queryClient.invalidateQueries({ queryKey: ['hr', 'positions'] });
    },
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Positions"
        description="Position titles used on employee records."
        actions={
          <>
            <ExportButton recordType="hr.position" href={exportHref(POSITIONS)} />
            <ImportButton recordType="hr.position" />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  create.reset();
                  setOpen(true);
                }}
              >
                New position
              </Button>
            )}
          </>
        }
      />
      {positions.isError && <ErrorState message={positions.error.message} />}
      <Section>
        {positions.isPending && <LoadingState label="Loading positions" />}
        {positions.data !== undefined && positions.data.items.length === 0 && (
          <EmptyState title="No positions yet" />
        )}
        {positions.data !== undefined && positions.data.items.length > 0 && (
          <Table size="small" aria-label="Positions">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {positions.data.items.map((position) => (
                <TableRow key={position.id}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/hr/positions/${position.id}`}
                      sx={{ fontWeight: 600 }}
                    >
                      {position.title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={position.status} />
                  </TableCell>
                  {canChange && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setEditing(position);
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <FormDialog
        open={open}
        title="New position"
        submitLabel="Create"
        canSubmit={title.trim() !== ''}
        submitting={create.isPending}
        error={create.isError ? create.error.message : null}
        onClose={() => {
          setOpen(false);
        }}
        onSubmit={() => {
          create.mutate();
        }}
      >
        <TextField
          label="Title"
          required
          autoFocus
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
      </FormDialog>
      {editing !== null && (
        <EditPositionDialog
          key={`${editing.id}:${String(editing.version)}`}
          position={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

/** Title and status through the API with If-Match; only what changed is sent. */
export function EditPositionDialog({
  position,
  onClose,
}: {
  position: PositionDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(position.title);
  const [status, setStatus] = useState<string>(position.status);
  const changes: Record<string, string> = {};
  if (title.trim() !== position.title) changes.title = title.trim();
  if (status !== position.status) changes.status = status;
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${POSITIONS}/${position.id}`,
        changes,
        PositionSchema,
        session.data?.csrfToken ?? '',
        position.version,
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['hr', 'positions'] });
    },
  });
  return (
    <FormDialog
      open
      title={`Edit position ${position.title}`}
      submitLabel="Save changes"
      canSubmit={title.trim() !== '' && Object.keys(changes).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Title"
        required
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
      />
      <TextField
        select
        label="Status"
        value={status}
        onChange={(e) => {
          setStatus(e.target.value);
        }}
      >
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="inactive">Inactive</MenuItem>
      </TextField>
    </FormDialog>
  );
}
